import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { ChevronDown, ChevronRight, Download, Loader2, History } from "lucide-react";
import { functions } from "../../firebase";
import { CLOUD_FUNCTIONS } from "../../constants/firebase";
import { useIssuedVersions, type SourceDocFamily } from "../../hooks/useIssuedVersions";
import type { IssuedVersion } from "../../types/issuedVersion";

export interface VersionHistoryProps {
  projectId: string;
  sourceDocFamily: SourceDocFamily;
  sourceDocId: string;
  /** When true, renders expanded by default. */
  defaultExpanded?: boolean;
  /** Optional legacy revision entries (e.g. DMP.revisionHistory) shown
   *  beneath new versions under a "Legacy revision notes" divider. */
  legacyEntries?: LegacyRevisionEntry[];
}

export interface LegacyRevisionEntry {
  version: string;
  date: Date | null;
  author: string;
  description: string;
}

export function VersionHistory(props: VersionHistoryProps) {
  const {
    projectId,
    sourceDocFamily,
    sourceDocId,
    defaultExpanded = false,
    legacyEntries,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { versions, loading, error } = useIssuedVersions({
    projectId,
    sourceDocFamily,
    sourceDocId,
  });

  const handleDownload = async (version: IssuedVersion) => {
    setDownloadError(null);
    setDownloadingId(version.id);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_VERSION_DOWNLOAD_URL);
      const response = await fn({
        projectId,
        sourceDocFamily,
        sourceDocId,
        versionId: version.id,
      });
      const data = response.data as { url: string; filename: string };
      // Trigger download via temporary anchor
      const link = document.createElement("a");
      link.href = data.url;
      if (data.filename) link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Failed to fetch download URL.",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const totalCount = versions.length + (legacyEntries?.length ?? 0);

  return (
    <div className="text-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <History className="h-4 w-4" />
        <span>
          History {totalCount > 0 ? `(${totalCount})` : ""}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100">
          {loading && (
            <div className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading version history…
            </div>
          )}

          {error && !loading && (
            <div className="px-3 py-3 text-sm text-red-600">
              Could not load history: {error.message}
            </div>
          )}

          {!loading && !error && versions.length === 0 && !legacyEntries?.length && (
            <div className="px-3 py-3 text-sm text-slate-500">
              No versions issued yet.
            </div>
          )}

          {versions.map((v) => (
            <div
              key={v.id}
              className="px-3 py-2.5 flex items-start justify-between gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 ring-1 ring-teal-200">
                    v{v.versionString}
                  </span>
                  <span className="text-xs text-slate-500">
                    {formatIssuedAt(v.issuedAt)}
                  </span>
                  <span className="text-xs text-slate-500">·</span>
                  <span className="text-xs text-slate-700">{v.issuedBy?.userName}</span>
                  {v.revisionType !== "initial" && (
                    <>
                      <span className="text-xs text-slate-500">·</span>
                      <span className="text-xs uppercase text-slate-500">
                        {v.revisionType}
                      </span>
                    </>
                  )}
                </div>
                {v.changeDescription && (
                  <div className="text-xs text-slate-600 mt-1">
                    {v.changeDescription}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDownload(v)}
                disabled={downloadingId === v.id}
                className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800 disabled:opacity-50"
                title={`Download ${v.filename}`}
              >
                {downloadingId === v.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Download
              </button>
            </div>
          ))}

          {legacyEntries && legacyEntries.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50">
                Legacy revision notes
              </div>
              {legacyEntries.map((entry, idx) => (
                <div
                  key={`legacy-${idx}`}
                  className="px-3 py-2.5 bg-slate-50/50"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      {entry.version}
                    </span>
                    {entry.date && (
                      <span className="text-xs text-slate-500">
                        {entry.date.toLocaleDateString()}
                      </span>
                    )}
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-700">{entry.author}</span>
                  </div>
                  {entry.description && (
                    <div className="text-xs text-slate-600 mt-1">
                      {entry.description}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {downloadError && (
            <div className="px-3 py-2 text-xs text-red-600 bg-red-50">
              {downloadError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatIssuedAt(ts: IssuedVersion["issuedAt"] | undefined): string {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts as unknown as string);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
